import useCheckSignOff from '../../hooks/useCheckSignOff';
import SelectAllButton from '../../components/SelectAllButton';
import React, { useState, useEffect, useRef } from "react";
import {
  FiArrowRight,
  FiCheckCircle,
  FiAlertCircle,
  FiCamera,
  FiFileText,
  FiChevronDown,
  FiUser,
} from "react-icons/fi";
import { LuFileClock, LuCopy } from "react-icons/lu";
// Updated to import your explicit staff list selector
import {
  selectStaffList,
  selectUsersLoading,
  selectUsersError,
} from "../../../../features/users/usersSlice";
import { useDispatch, useSelector } from "react-redux";
import { getUsersAsync } from "../../../../features/users/usersActions";
import {
  fetchChecklistBySlug,
  submitChecklistData,
  fetchSubmissionByDate,
  submitChecklistResult,
  submitBulletResults,
  fetchChecklistDetail,
} from "../../../../features/checks/checkActions.js";
import AdditionalChecks from "./components/OpeningCheckForm/AdditionalChecks";
import { DynamicIcon } from "../../../../components/DynamicIcon";
import Loader from "../../../../components/Loader";
import { getAssetUrl } from "../../../../components/AssetUrl";
import { Camera, Trash2 } from "lucide-react";
import Toast from "../../../../components/Toast";
import IncidentSelection from "../../../incident-log/components/IncidentSelection.js";
import { createPortal } from "react-dom";

const OpeningCheckForm = ({ slug, selectedDate }) => {
  const dispatch = useDispatch();
  const typingTimeoutRef = useRef(null);
  const [selectingAll, setSelectingAll] = useState({});
  const selectAllInFlight = useRef(new Set());
  const bulkResource = `${slug}:${selectedDate}`;
  const bulkResourceRef = useRef(bulkResource);
  bulkResourceRef.current = bulkResource;
  useEffect(() => {
    bulkResourceRef.current = bulkResource;
    setSelectingAll({});
    return () => { bulkResourceRef.current = null; };
  }, [bulkResource]);

  const [notes, setNotes] = useState("");
  const [approved, setApproved] = useState(false);

  // Use the dedicated staff selector to extract backend-filtered users
  const users = useSelector(selectStaffList);
  const isUsersLoading = useSelector(selectUsersLoading);
  const isUsersError = useSelector(selectUsersError);

  const dropdownRef = useRef(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [issueNotes, setIssueNotes] = useState({});
  const issueNotesRef = useRef(null);

  const [createIncidentToggles, setCreateIncidentToggles] = useState({});
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const checkList = useSelector(
    (state) => state.checks.checklistsBySlug[slug] || [],
  );
  const checklistIconColor = `text-${checkList?.color || "indigo"}-600`;
  const isChecklistLoading = useSelector((state) => state.checks.loading);
  const isChecklistError = useSelector((state) => state.checks.error);
  const submissionData = useSelector(state => state.checks.submissionsByDate[`${checkList?.id}:${selectedDate}`]?.data || null);
  const activeChecklistData = useSelector((state) => state.checklist);
  const isChecksLoading = useSelector((state) => state.checks.loading);
  const isChecksError = useSelector((state) => state.checks.error);

  const [signature, setSignature] = useState("");
  const signatureTimeoutRef = useRef(null);

  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [bulletCheckedState, setBulletCheckedState] = useState({});
  const [itemStatusState, setItemStatusState] = useState({});

  const checklistTitle = checkList?.name || "Checklist";
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [issuePhotoState, setIssuePhotoState] = useState({});
  const [issuePhotoChoiceItem, setIssuePhotoChoiceItem] = useState(null);

  const issuePhotoGalleryInputRef = useRef(null);
  const issuePhotoCameraInputRef = useRef(null);

  const getBulletResult = (itemId, bulletId) => {
    return submissionData?.bulletResults?.find(
      (result) =>
        result.checklistItemId === itemId && result.bulletId === bulletId,
    );
  };

  const [bulletsDropdownOpen, setBulletsDropdownOpen] = useState({});
  const [bulletPhotosState, setBulletPhotosState] = useState({}); // itemId -> [{id, url}]
  const [activeBulletUploadItem, setActiveBulletUploadItem] = useState(null);
  const bulletPhotoInputRef = useRef(null);

  const [isSavingStaff, setIsSavingStaff] = useState(false);

  // === ALL REACT HOOKS PLACED SECURELY AT THE TOP ===

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 1. Fetch Staff Personnel Records (Runs once on component mount)
  useEffect(() => {
    const loadStaff = async () => {
      try {
        await dispatch(getUsersAsync("STAFF")).unwrap();
      } catch (err) {
        console.error("Failed to load staff list:", err);
      }
    };
    loadStaff();
  }, [dispatch]);

  // 2. Fetch Checklist Template Definition Layout (Runs when the route slug changes)
  useEffect(() => {
    const loadTemplate = async () => {
      if (!slug) return;
      try {
        await dispatch(fetchChecklistBySlug(slug)).unwrap();
      } catch (err) {
        console.error(`Failed to load template layout for slug: ${slug}`, err);
      }
    };
    loadTemplate();
  }, [dispatch, slug]);

  // 3. Fetch Master Log Row for Today (Runs once the Checklist Template Profile ID resolves)
  useEffect(() => {
    const loadTodaySubmission = async () => {
      if (!checkList?.id) return;
      try {
        await dispatch(
          fetchSubmissionByDate({
            checklistId: checkList.id,
            creationDate: selectedDate,
          }),
        ).unwrap();
      } catch (err) {
        console.error(
          "Failed to locate or instantiate today's submission container:",
          err,
        );
      }
    };
    loadTodaySubmission();
  }, [dispatch, checkList?.id, selectedDate]);

  // 4. Fetch Synchronized Live Cell Answers (Runs when the Master Log Container row resolves)
  useEffect(() => {
    const loadLiveAnswers = async () => {
      if (!submissionData?.id) {
        setSelectedUser(null);
        setApproved(false);
        setNotes("");
        setSignature("");
        setPhotoPreview(null);
        setIssueNotes({});
        setIssuePhotoState({});
        setCreateIncidentToggles({});
        setBulletCheckedState({});
        setItemStatusState({});
        return;
      }

      try {
        setSelectedUser(submissionData.completedBy);
        setApproved(submissionData.isClosedSecured);
        setNotes(submissionData.openingNotes || "");
        setSignature(submissionData.managerSignature || "");
        setPhotoPreview(
          submissionData.photoUrl ? getAssetUrl(submissionData.photoUrl) : null,
        );

        // Load saved issue comments
        const savedIssueNotes = {};

        // Load saved issue photos
        const savedIssuePhotos = {};

        submissionData.itemResults?.forEach((result) => {
          const itemId = result.checklistItemId;

          if (result.comment) {
            savedIssueNotes[itemId] = result.comment;
          }

          if (result.issuePhotoUrl) {
            savedIssuePhotos[itemId] = getAssetUrl(result.issuePhotoUrl);
          }
        });

        setIssueNotes(savedIssueNotes);
        setIssuePhotoState(savedIssuePhotos);
      } catch (err) {
        console.error(
          `Failed to stream existing answers for submission ID: ${submissionData.id}`,
          err,
        );
      }
    };

    loadLiveAnswers();
  }, [submissionData]);

  const handleIssuePhotoClick = (itemId) => {
    setIssuePhotoChoiceItem(itemId);
  };

  const handleIssuePhotoSelected = async (itemId, file) => {
    if (!file) return;

    // Basic validation
    if (!file.type.startsWith("image/")) {
      Toast("error", "Please select an image file.");
      return;
    }

    // 5MB maximum
    if (file.size > 5 * 1024 * 1024) {
      Toast("error", "Image size must be less than 5MB.");
      return;
    }

    // Immediate local preview
    const localUrl = URL.createObjectURL(file);

    setIssuePhotoState((prev) => ({
      ...prev,
      [itemId]: localUrl,
    }));

    setIssuePhotoChoiceItem(null);

    if (!submissionData?.id) {
      Toast("error", "No active checklist submission found.");
      return;
    }

    const formData = new FormData();

    formData.append("submissionId", String(submissionData.id));

    formData.append("checklistItemId", String(itemId));

    formData.append("issuePhoto", file);

    try {
      const result = await dispatch(submitChecklistResult(formData)).unwrap();

      // If backend returns the final uploaded URL,
      // replace temporary blob preview with permanent URL.
      if (result?.data?.issuePhotoUrl) {
        setIssuePhotoState((prev) => ({
          ...prev,
          [itemId]: getAssetUrl(result.data.issuePhotoUrl),
        }));
      }

      // Refresh detail/submission if required
      await dispatch(
        fetchSubmissionByDate({
          checklistId: checkList.id,
          creationDate: selectedDate,
        }),
      ).unwrap();
    } catch (error) {
      console.error(`Failed to upload issue photo for item ${itemId}:`, error);

      setIssuePhotoState((prev) => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });

      Toast("error", error || "Failed to upload issue photo.");
    } finally {
      URL.revokeObjectURL(localUrl);
    }
  };

  const handleRemoveIssuePhoto = async (itemId) => {
    const previousPhoto = issuePhotoState[itemId];

    // Immediately remove from UI
    setIssuePhotoState((prev) => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });

    if (!submissionData?.id) return;

    const formData = new FormData();

    formData.append("submissionId", String(submissionData.id));

    formData.append("checklistItemId", String(itemId));

    // Empty string tells backend to clear the photo
    formData.append("issuePhotoUrl", "");

    try {
      await dispatch(submitChecklistResult(formData)).unwrap();
    } catch (error) {
      console.error("Failed to remove issue photo:", error);

      // Restore if backend failed
      setIssuePhotoState((prev) => ({
        ...prev,
        [itemId]: previousPhoto,
      }));

      Toast("error", "Failed to remove issue photo.");
    }
  };

  // === INTERACTIVE HANDLERS ===
  const handleSelectUser = async (user) => {
    if (checkList.id && user.id) {
      const formData = new FormData();
      setIsSavingStaff(true);

      formData.append("checklistId", Number(checkList.id));
      formData.append("completedById", Number(user.id));
      formData.append("creationDate", selectedDate);

      try {
        // Submit the checklist
        await dispatch(submitChecklistData(formData)).unwrap();
        setSelectedUser(user);
        setIsDropdownOpen(false);
      } catch (error) {
        console.error("Failed to complete checklist submission:", error);
      } finally {
        setIsSavingStaff(false);
      }
    }
  };

  const handleCheckboxToggle = (isChecked) => {
    setApproved(isChecked);

    if (checkList.id) {
      // 1. Initialize a native browser FormData container
      const formData = new FormData();

      // 2. Append parameters individually as required by multipart/form-data
      formData.append("checklistId", Number(checkList.id));
      formData.append("creationDate", selectedDate);
      formData.append("isClosedSecured", isChecked); // Transmits cleanly as "true" or "false" string

      // 3. Dispatch the complete FormData instance to your Redux thunk
      dispatch(submitChecklistData(formData));
    }
  };

  const handleStatusChange = async (itemId, status) => {
    if (!selectedUser) {
      Toast("error", "Please select a staff member to complete this check");
      return;
    }

    // Create unique key for this item
    const itemKey = String(itemId);

    // Save previous status in case API fails
    const previousStatus =
      itemStatusState[itemKey] ??
      submissionData?.itemResults?.find(
        (result) => result.checklistItemId === itemId,
      )?.status ??
      null;

    // Immediately update UI
    setItemStatusState((prev) => ({
      ...prev,
      [itemKey]: status,
    }));

    const payload = {
      submissionId: submissionData.id,
      checklistItemId: itemId,
      status: status,
    };

    try {
      await dispatch(submitChecklistResult(payload)).unwrap();

      await dispatch(
        fetchChecklistDetail({
          slug,
          creationDate: selectedDate,
        }),
      ).unwrap();
    } catch (error) {
      console.error("Failed to sync item status:", error);

      // Revert UI if API fails
      setItemStatusState((prev) => ({
        ...prev,
        [itemKey]: previousStatus,
      }));
    }
  };

  const handleNotesChange = (e) => {
    const newText = e.target.value;
    setNotes(newText);

    // 2. Clear the previous timer because the user is still actively typing
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // 3. Start a fresh 1-second countdown
    typingTimeoutRef.current = setTimeout(() => {
      if (checkList?.id) {
        // Create FormData container for multipart form transmission
        const formData = new FormData();

        formData.append("checklistId", Number(checkList.id));
        formData.append("creationDate", selectedDate);
        formData.append("openingNotes", newText);

        // Dispatch the clean FormData payload instance
        dispatch(submitChecklistData(formData));
      }
    }, 1000);
  };

  // Good practice: Clean up the timer if the user unmounts the page mid-countdown
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  const handleSignatureChange = (e) => {
    const newSignature = e.target.value;
    setSignature(newSignature); // 1. Update local screen state instantly

    // 2. Wipe previous timeout if manager is still typing actively
    if (signatureTimeoutRef.current) {
      clearTimeout(signatureTimeoutRef.current);
    }

    // 3. Set a clean 1-second delay before pushing data to database
    signatureTimeoutRef.current = setTimeout(() => {
      if (checkList?.id) {
        // Create FormData container for multipart form transmission
        const formData = new FormData();

        formData.append("checklistId", Number(checkList.id));
        formData.append("creationDate", selectedDate);
        formData.append("managerSignature", newSignature);

        // Dispatch the clean FormData payload instance
        dispatch(submitChecklistData(formData));
      }
    }, 1000);
  };

  // Clear the timer if page is destroyed mid-typing session
  useEffect(() => {
    return () => {
      if (signatureTimeoutRef.current)
        clearTimeout(signatureTimeoutRef.current);
    };
  }, []);

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 1. Show instant local preview for a fast UI experience
    const localUrl = URL.createObjectURL(file);
    setPhotoPreview(localUrl);

    // 2. Dispatch the image data to your backend immediately
    if (checkList?.id) {
      const formData = new FormData();
      formData.append("checklistId", Number(checkList.id));
      formData.append("creationDate", selectedDate);
      formData.append("photo", file); // 'photo' matches uploadSingleFile('photo') on backend

      try {
        await dispatch(submitChecklistData(formData)).unwrap();
        console.log("Photo uploaded and synced successfully!");
      } catch (error) {
        console.error("Failed to upload photo:", error);
        // Fallback: Reset preview if the network upload fails
        setPhotoPreview(null);
      }
    } else {
      console.warn("Cannot upload: checklist ID not resolved yet.");
    }
  };

  const handleRemovePhoto = async (e) => {
    e.stopPropagation(); // Stops the file browser dialog from reopening

    // 1. Instantly clear out local preview elements
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    // 2. Dispatch to backend to erase the existing photo reference string
    if (checkList?.id) {
      const formData = new FormData();
      formData.append("checklistId", Number(checkList.id));
      formData.append("creationDate", selectedDate);
      formData.append("photoUrl", ""); // Sending an empty string clears the column in Prisma

      try {
        await dispatch(submitChecklistData(formData)).unwrap();
        console.log("Photo reference deleted from database successfully.");
      } catch (error) {
        console.error(
          "Failed to update database photo reference status:",
          error,
        );
      }
    }
  };

  const handleIssueNoteChange = (itemId, newText) => {
    // 1. Immediately update local screen state to guarantee a fluid typing feel
    setIssueNotes((prev) => ({
      ...prev,
      [itemId]: newText,
    }));

    // --- DYNAMIC INITIALIZATION PIPELINE ---
    // If the ref value is currently null, instantiate it as an empty object layout container
    if (issueNotesRef.current === null) {
      issueNotesRef.current = {};
    }

    // 2. Clear the specific timer for this item ID if the user is actively typing
    if (issueNotesRef.current[itemId]) {
      clearTimeout(issueNotesRef.current[itemId]);
    }

    // 3. Verify core structural dependency conditions exist before spinning up a sync thread
    if (!submissionData || !submissionData.id) {
      console.error(
        "Cannot sync comment change: No active master submission row container found.",
      );
      return;
    }

    // 4. Start a fresh 1-second countdown for this specific item row object
    issueNotesRef.current[itemId] = setTimeout(async () => {
      const payload = {
        submissionId: Number(submissionData.id),
        checklistItemId: Number(itemId),
        comment: newText,
      };

      try {
        await dispatch(submitChecklistResult(payload)).unwrap();
        console.log(`Comment for item ${itemId} synchronized successfully.`);
      } catch (error) {
        console.error(
          `Failed to synchronize comment value update for item ${itemId}:`,
          error,
        );
      }
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (issueNotesRef.current) issueNotesRef.current = null;
    };
  }, []);

  const handleAddAccident = (itemId) => {
    console.log(
      "Add Accident triggered for item:",
      itemId,
      "Note:",
      issueNotes[itemId],
    );
  };

  const handleToggleIncident = (itemId) => {
    setCreateIncidentToggles((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));

    setIsModalOpen(true);
  };

  const toggleBulletsDropdown = (itemId) => {
    setBulletsDropdownOpen((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const handleBulletUploadClick = (itemId) => {
    setActiveBulletUploadItem(itemId);
    setTimeout(() => bulletPhotoInputRef.current?.click(), 0);
  };

  const handleBulletPhotosSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const itemId = activeBulletUploadItem;
    if (!files.length || !itemId) return;

    // Instant local previews
    const newPreviews = files.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      url: URL.createObjectURL(file),
    }));

    setBulletPhotosState((prev) => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), ...newPreviews],
    }));

    if (!submissionData?.id) {
      Toast("error", "No active checklist submission found.");
      return;
    }

    const formData = new FormData();
    formData.append("submissionId", String(submissionData.id));
    formData.append("checklistItemId", String(itemId));
    files.forEach((file) => formData.append("bulletPhotos", file));

    try {
      const result = await dispatch(submitChecklistResult(formData)).unwrap();

      if (result?.data?.bulletPhotoUrls?.length) {
        setBulletPhotosState((prev) => ({
          ...prev,
          [itemId]: result.data.bulletPhotoUrls.map((url, idx) => ({
            id: `${itemId}-server-${idx}`,
            url: getAssetUrl(url),
          })),
        }));
      }

      await dispatch(
        fetchSubmissionByDate({
          checklistId: checkList.id,
          creationDate: selectedDate,
        }),
      ).unwrap();
    } catch (error) {
      console.error(
        `Failed to upload bullet photos for item ${itemId}:`,
        error,
      );
      Toast("error", error || "Failed to upload photos.");
    } finally {
      newPreviews.forEach((p) => URL.revokeObjectURL(p.url));
      setActiveBulletUploadItem(null);
    }
  };

  const handleRemoveBulletPhoto = (itemId, photoId) => {
    setBulletPhotosState((prev) => ({
      ...prev,
      [itemId]: (prev[itemId] || []).filter((p) => p.id !== photoId),
    }));
    // Add a backend call here if you need to persist removals server-side.
  };

  const handleBulletCheckboxChange = async (itemId, bulletId, checked) => {
    if (!selectedUser) {
      Toast("error", "Please select a staff member to complete this check");
      return;
    }

    const key = `${itemId}-${bulletId}`;

    // Immediately update UI
    setBulletCheckedState((prev) => ({
      ...prev,
      [key]: checked,
    }));

    const formData = new FormData();

    formData.append("submissionId", String(submissionData.id));

    formData.append("checklistItemId", String(itemId));

    formData.append("bulletId", String(bulletId));

    formData.append("isChecked", String(checked));

    try {
      await dispatch(submitBulletResults(formData)).unwrap();
    } catch (error) {
      console.error("Failed to sync bullet checkbox:", error);

      // Revert UI if API fails
      setBulletCheckedState((prev) => ({
        ...prev,
        [key]: !checked,
      }));
    }
  };

  const handleSelectAllBullets = async item => {
    if (!selectedUser || !submissionData?.id || isSavingStaff) return;
    const resource = bulkResource;
    const flightKey = `${resource}:${item.id}`;
    if (selectAllInFlight.current.has(flightKey)) return;
    selectAllInFlight.current.add(flightKey);
    setSelectingAll(previous => ({ ...previous, [item.id]: true }));
    setBulletsDropdownOpen(previous => ({ ...previous, [item.id]: true }));
    try {
      for (const bullet of item.bullets || []) {
        if (bulkResourceRef.current !== resource) break;
        const key = `${item.id}-${bullet.id}`;
        if (bulletCheckedState[key] ?? getBulletResult(item.id, bullet.id)?.isChecked ?? false) continue;
        const data = new FormData();
        data.append('submissionId', String(submissionData.id));
        data.append('checklistItemId', String(item.id));
        data.append('bulletId', String(bullet.id));
        data.append('isChecked', 'true');
        await dispatch(submitBulletResults(data)).unwrap();
        if (bulkResourceRef.current === resource) setBulletCheckedState(previous => ({ ...previous, [key]: true }));
      }
    } catch (error) {
      if (bulkResourceRef.current === resource) Toast('error', 'Unable to select all points. Saved points were retained; please try again.');
    } finally {
      selectAllInFlight.current.delete(flightKey);
      if (bulkResourceRef.current === resource) setSelectingAll(previous => ({ ...previous, [item.id]: false }));
    }
  };

  const signOff = useCheckSignOff({
    checklistId: checkList?.id, creationDate: selectedDate, approved: approved === true && !Object.values(selectingAll).some(Boolean),
    fields: { openingNotes: notes, managerSignature: signature, completedById: selectedUser?.id },
  });

  // === VALIDATION RUNTIME EARLY RETURNS BOTTOM OF FILE ===
  // if (isUsersLoading) return <div>Loading User records...</div>;
  if (isUsersError)
    return (
      <div style={{ color: "red" }}>Error fetching users: {isUsersError}</div>
    );
  // if (isChecksLoading) return <div>Loading checklist records...</div>;
  if (isChecksError)
    return (
      <div style={{ color: "red" }}>
        Error fetching checklist: {isChecksError}
      </div>
    );

  return (
    <form onSubmit={signOff.submit} className="w-full py-4 font-sans bg-slate-50/30 flex flex-col gap-6">
      {/* Issue Photo Inputs */}

      <input
        ref={issuePhotoCameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];

          if (file && issuePhotoChoiceItem) {
            handleIssuePhotoSelected(issuePhotoChoiceItem, file);
          }

          e.target.value = "";
        }}
      />

      <input
        ref={issuePhotoGalleryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];

          if (file && issuePhotoChoiceItem) {
            handleIssuePhotoSelected(issuePhotoChoiceItem, file);
          }

          e.target.value = "";
        }}
      />

      <input
        ref={bulletPhotoInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleBulletPhotosSelected}
      />

      {/* SECTION 2: Table Section Titles */}
      <div className="flex items-center justify-between px-2 pt-2 border-b border-slate-100 pb-2">
        <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
          {checklistTitle} Items
        </h3>
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`w-full flex items-center justify-between px-3 py-2 bg-white border rounded-lg text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${isDropdownOpen
                ? "border-blue-500 shadow-sm"
                : "border-slate-200 hover:border-slate-300"
              }`}
          >
            <div className="flex items-center gap-2 text-slate-700">
              <FiUser
                className={selectedUser ? "text-blue-500" : "text-slate-400"}
              />
              <span
                className={
                  selectedUser ? "font-medium text-slate-900" : "text-slate-400"
                }
              >
                {selectedUser
                  ? selectedUser.fullName
                  : "Select Staff Member..."}
              </span>
            </div>
            <FiChevronDown
              className={`text-slate-400 transition-transform duration-150 ${isDropdownOpen ? "rotate-180 text-blue-500" : ""}`}
            />
          </button>

          {isDropdownOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto py-1">
              {users && users.length > 0 ? (
                users.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    disabled={isSavingStaff}
                    onClick={() => handleSelectUser(user)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors ${isSavingStaff
                        ? "opacity-50 cursor-not-allowed"
                        : selectedUser?.id === user.id
                          ? "bg-blue-50 text-blue-700 font-medium"
                          : "text-slate-700 hover:bg-slate-50"
                      }`}
                  >
                    <span className="truncate">{user.fullName}</span>

                    {isSavingStaff && selectedUser?.id !== user.id ? (
                      <span className="w-4 h-4 ml-2 border-2 border-slate-200 border-t-blue-500 rounded-full animate-spin shrink-0" />
                    ) : selectedUser?.id === user.id ? (
                      <FiCheckCircle className="text-blue-500 shrink-0 ml-2" />
                    ) : null}
                  </button>
                ))
              ) : (
                <div className="px-3 py-3 text-xs text-center text-slate-400 italic">
                  No users found
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* SECTION 3: Checklist Items Mapping Loop */}
      <div className="flex flex-col gap-4">
        {checkList.items &&
          checkList.items.map((item) => {
            const savedItemStatus = submissionData?.itemResults?.find(
              (result) => result.checklistItemId === item.id,
            )?.status;

            const currentItemStatus =
              itemStatusState[item.id] ?? savedItemStatus ?? null;

            return (
              <div
                key={item.id}
                className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm"
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  {/* Left Content Area */}
                  <div className="flex min-w-0 flex-1 gap-3 w-full md:w-auto">
                    <div className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                      <DynamicIcon
                        iconName={item.iconName}
                        className={checklistIconColor}
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <h4 className="text-xs font-bold text-slate-800 leading-snug">
                        {item.title}
                      </h4>
                      <p className="text-[11px] text-slate-400 leading-relaxed font-medium">
                        {item.description}
                      </p>

                      {item.bullets && item.bullets.length > 0 && (
                        <div className="mt-2 border border-slate-100 rounded-lg overflow-hidden">
                          {/* Dropdown header */}
                          <button
                            type="button"
                            onClick={() => toggleBulletsDropdown(item.id)}
                            className="w-full flex items-center justify-between px-3 py-2 bg-slate-50/60 hover:bg-slate-100 transition-colors text-[11px] font-semibold text-slate-600"
                          >
                            <span>
                              {item.bullets.length} checklist point
                              {item.bullets.length > 1 ? "s" : ""}
                            </span>
                            <FiChevronDown
                              className={`text-slate-400 transition-transform duration-150 ${bulletsDropdownOpen[item.id] ? "rotate-180" : ""
                                }`}
                            />
                          </button>

                          {bulletsDropdownOpen[item.id] && (
                            <div className="flex flex-col md:flex-row gap-3 p-3 bg-white">
                              {/* LEFT: bullet checklist */}
                              <ul className="flex-1 flex flex-col gap-2 pl-1">
                                {item.bullets.map((bullet, idx) => {
                                  const bulletId = bullet.id || idx;
                                  const bulletText =
                                    typeof bullet === "object"
                                      ? bullet.text
                                      : bullet;
                                  const bulletResult = getBulletResult(
                                    item.id,
                                    bulletId,
                                  );
                                  const bulletKey = `${item.id}-${bulletId}`;
                                  const isBulletChecked =
                                    bulletCheckedState[bulletKey] ??
                                    bulletResult?.isChecked ??
                                    false;

                                  return (
                                    <li
                                      key={bulletId}
                                      className="flex items-start gap-1.5 text-[10px] text-slate-500 font-medium leading-relaxed p-1 rounded hover:bg-slate-50 transition-colors"
                                    >
                                      <input
                                        type="checkbox"
                                        disabled={selectingAll[item.id]}
                                        id={`bullet-${item.id}-${bulletId}`}
                                        checked={isBulletChecked}
                                        onChange={(e) =>
                                          handleBulletCheckboxChange(
                                            item.id,
                                            bulletId,
                                            e.target.checked,
                                          )
                                        }
                                        className="accent-slate-500 rounded border-slate-300 mt-0.5 cursor-pointer h-3 w-3"
                                      />
                                      <label
                                        htmlFor={`bullet-${item.id}-${bulletId}`}
                                        className="cursor-pointer select-none"
                                      >
                                        {bulletText}
                                      </label>
                                    </li>
                                  );
                                })}
                              </ul>

                              {/* RIGHT: multi-photo upload + grid */}
                              <div className="w-full md:w-56 flex flex-col gap-2 border-t md:border-t-0 md:border-l border-slate-100 pt-3 md:pt-0 md:pl-3">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleBulletUploadClick(item.id)
                                  }
                                  className="flex items-center justify-center gap-1.5 text-xs font-semibold text-blue-600 border border-dashed border-slate-200 rounded-lg py-2 hover:bg-slate-50 transition-colors"
                                >
                                  <FiCamera className="text-sm" />
                                  <span>Add photos</span>
                                </button>

                                {(bulletPhotosState[item.id] || []).length >
                                  0 && (
                                    <div className="grid grid-cols-3 gap-1.5">
                                      {bulletPhotosState[item.id].map((photo) => (
                                        <div
                                          key={photo.id}
                                          className="relative aspect-square rounded-md overflow-hidden border border-slate-200 bg-slate-100 group"
                                        >
                                          <img
                                            src={photo.url}
                                            alt="Bullet upload"
                                            className="w-full h-full object-cover"
                                          />
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleRemoveBulletPhoto(
                                                item.id,
                                                photo.id,
                                              )
                                            }
                                            className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] opacity-0 group-hover:opacity-100 transition-opacity"
                                          >
                                            ✕
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 self-end md:self-start">
                    {/* Right Status Action Toggles */}
                    <div className="flex flex-nowrap items-center gap-2 whitespace-nowrap">
                      {item.bullets?.length > 0 && <SelectAllButton section={item.title} onClick={() => handleSelectAllBullets(item)} busy={selectingAll[item.id]}
                        disabled={!selectedUser || !submissionData?.id || isSavingStaff || signOff.isSubmitting ||
                          submissionData.creationDate !== selectedDate || submissionData.checklistId !== checkList.id ||
                          item.bullets.every(bullet => bulletCheckedState[`${item.id}-${bullet.id}`] ?? getBulletResult(item.id, bullet.id)?.isChecked ?? false)} />}
                      {/* PASS */}
                      <button
                        type="button"
                        onClick={() => handleStatusChange(item.id, "PASS")}
                        className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border font-semibold text-xs min-w-[76px] transition-all shadow-sm ${currentItemStatus === "PASS"
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white border-slate-200 text-slate-600 hover:border-emerald-200 hover:text-emerald-600"
                          }`}
                      >
                        <FiCheckCircle className="text-xs" />
                        <span>Pass</span>
                      </button>

                      {/* ISSUE */}
                      <button
                        type="button"
                        onClick={() => handleStatusChange(item.id, "ISSUE")}
                        className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border font-semibold text-xs min-w-[76px] transition-all shadow-sm ${currentItemStatus === "ISSUE"
                            ? "bg-red-500 text-white border-red-500"
                            : "bg-white border-slate-200 text-slate-600 hover:border-red-200 hover:text-red-500"
                          }`}
                      >
                        <FiAlertCircle className="text-xs" />
                        <span>Issue</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div>
                  {currentItemStatus === "ISSUE" && (
                    <div className="mt-3 p-4 bg-red-50/30 border border-red-100 rounded-xl flex flex-col md:flex-row gap-4 items-stretch">
                      {/* Header banner for mobile/stack layout */}
                      <div className="w-full flex items-center gap-1.5 text-xs font-semibold text-red-600 md:hidden">
                        <FiAlertCircle className="text-sm" />
                        <span>Issue selected</span>
                      </div>

                      {/* Comments Box */}
                      <div className="flex-1 flex flex-col gap-1.5">
                        <div className="hidden md:flex items-center gap-1.5 text-xs font-semibold text-red-600 mb-0.5">
                          <FiAlertCircle className="text-sm" />
                          <span>Issue selected</span>
                        </div>
                        <label className="text-[11px] font-medium text-slate-500">
                          Comments{" "}
                          <span className="text-slate-400 text-[10px]">
                            (optional)
                          </span>
                        </label>
                        <div className="relative flex-1">
                          <textarea
                            value={issueNotes[item.id] || ""}
                            onChange={(e) =>
                              handleIssueNoteChange(item.id, e.target.value)
                            }
                            placeholder="Describe the issue..."
                            maxLength={250}
                            className="w-full h-full min-h-[80px] border border-slate-200 rounded-xl p-3 text-xs text-slate-700 bg-white placeholder-slate-400 focus:outline-none focus:border-red-400 resize-none shadow-sm"
                          />
                          <span className="absolute bottom-2 right-3 text-[10px] text-slate-400">
                            {(issueNotes[item.id] || "").length}/250
                          </span>
                        </div>
                      </div>

                      {/* Issue Photo Upload Box */}
                      <div className="w-full md:w-64 flex flex-col gap-1.5">
                        <label className="text-[11px] font-medium text-slate-500 md:mt-[22px]">
                          Photo{" "}
                          <span className="text-slate-400 text-[10px]">
                            (optional)
                          </span>
                        </label>

                        {issuePhotoState[item.id] ? (
                          <div className="relative flex-1 min-h-[120px] rounded-xl overflow-hidden border border-slate-200 bg-slate-100 group">
                            <img
                              src={issuePhotoState[item.id]}
                              alt="Issue"
                              className="w-full h-full min-h-[120px] object-cover"
                            />

                            {/* Overlay */}
                            <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              {/* Change */}
                              <button
                                type="button"
                                onClick={() => handleIssuePhotoClick(item.id)}
                                className="px-3 py-1.5 bg-white text-slate-800 rounded-lg text-xs font-semibold shadow hover:bg-slate-50"
                              >
                                Change
                              </button>

                              {/* Delete */}
                              <button
                                type="button"
                                onClick={() => handleRemoveIssuePhoto(item.id)}
                                className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-semibold shadow hover:bg-red-700"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleIssuePhotoClick(item.id)}
                            className="flex-1 flex flex-col items-center justify-center gap-1 border border-dashed border-slate-200 bg-white hover:bg-slate-50 rounded-xl p-4 transition-colors min-h-[80px]"
                          >
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-600">
                              <FiCamera className="text-sm" />
                              <span>Add photo</span>
                            </div>

                            <span className="text-[10px] text-slate-400">
                              JPG, PNG or WEBP (Max 5MB)
                            </span>
                          </button>
                        )}
                      </div>

                      {/* Create Incident Toggle Box */}
                      <div className="w-full md:w-56 border border-slate-200 bg-white rounded-xl p-4 flex flex-col justify-between gap-3 shadow-sm">
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-bold text-slate-800">
                            Create Incident
                          </span>
                          <p className="text-[11px] text-slate-400 leading-normal">
                            If this issue requires further action or follow up,
                            create an incident.
                          </p>
                        </div>
                        <div className="flex items-center gap-2 pt-2 border-t border-slate-50">
                          <button
                            type="button"
                            onClick={() => handleToggleIncident(item.id)}
                            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${createIncidentToggles[item.id]
                                ? "bg-blue-600"
                                : "bg-slate-300"
                              }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${createIncidentToggles[item.id]
                                  ? "translate-x-4"
                                  : "translate-x-0"
                                }`}
                            />
                          </button>
                          <span className="text-[11px] font-medium text-slate-600">
                            Create incident
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {/* Section 4 Additional Checks Section */}
      <AdditionalChecks />

      {/* SECTION 5: Manager Sign-off Input Block */}
      <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm mt-2 flex flex-col gap-5">
        {/* Module Title Header Label */}
        <div className="flex items-start gap-2.5">
          <div className="w-5 h-5 rounded bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 mt-0.5">
            <FiFileText className="text-xs" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-slate-800">
              {checklistTitle} Notes & Sign-off
            </h4>
          </div>
        </div>

        {/* Input Fields Row Layout Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Opening Notes */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
              {checklistTitle} notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={handleNotesChange}
              placeholder={`Add any notes about today's ${checklistTitle.toLowerCase()}...`}
              className="w-full border border-slate-200 rounded-xl p-3 text-xs text-slate-700 bg-white placeholder-slate-300 focus:outline-none focus:border-indigo-500 min-h-[76px] resize-none shadow-inner"
            />
          </div>

          {/* Upload Attachments */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
              Add photo (optional)
            </label>

            {/* Hidden Native File Input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handlePhotoChange}
              accept="image/*"
              className="hidden"
            />

            {photoPreview ? (
              /* Photo Preview State View Container Layout Box */
              <div className="relative w-full border border-slate-200 bg-slate-100 rounded-xl h-[140px] overflow-hidden group">
                <img
                  src={photoPreview}
                  alt="Submission preview"
                  className="w-full h-full object-cover"
                />
                {/* Hover overlay utility curtain panels */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-1.5 bg-white text-slate-800 rounded-lg text-xs font-bold shadow hover:bg-slate-50"
                  >
                    Change
                  </button>
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-bold shadow hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ) : (
              /* Fallback Interactive Blank Empty State Target Control Slot */
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-full border border-dashed border-slate-200 bg-slate-50/50 rounded-xl p-4 flex flex-col items-center justify-center gap-2 h-[76px] cursor-pointer hover:bg-slate-50 hover:border-slate-300 transition-colors"
              >
                <div className="flex items-center gap-1.5 text-indigo-600 font-bold text-xs">
                  <FiCamera className="text-sm" />
                  <span>Upload photo</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Manager Handwritten Validation Block */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-slate-50 items-end">
          {/* Signature Box */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
              Manager signature
            </label>
            <div className="flex items-center gap-4 bg-slate-50/50 border border-slate-100 rounded-xl p-3 h-14 relative overflow-hidden">
              <input
                type="text"
                value={signature || ""}
                onChange={handleSignatureChange}
                placeholder="Type name to sign..."
                className="font-serif italic text-xl text-slate-700 bg-transparent pl-2 tracking-wide focus:outline-none w-full pr-12 placeholder:text-slate-300 placeholder:not-italic placeholder:text-xs placeholder:font-sans placeholder:font-normal"
              />
              {signature && (
                <button
                  type="button"
                  onClick={() =>
                    handleSignatureChange({ target: { value: "" } })
                  } // Reuse sync handler to wipe out database record cleanly
                  className="absolute right-3 text-[10px] text-red-500 hover:text-red-600 font-bold tracking-wide"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Secure Checkbox Confirmation */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
              Confirm restaurant is closed and secured
            </label>
            <label className="flex items-center gap-2.5 bg-slate-50/50 border border-slate-100 rounded-xl p-3 h-14 cursor-pointer hover:bg-slate-50 transition-colors px-4">
              <input
                type="checkbox"
                checked={approved === true}
                disabled={signOff.isSubmitting}
                onChange={(e) => handleCheckboxToggle(e.target.checked)} // Hooked to sync state real-time
                className="w-4 h-4 rounded text-blue-600 border-slate-300 focus:ring-blue-500 cursor-pointer"
              />
              <span className="text-xs font-semibold text-slate-700 select-none">
                Approve
              </span>
            </label>
          </div>
        </div>

        {/* Bottom Submission Action Footer Bar Button */}
        <button
          type="submit"
          disabled={!signOff.canSubmit || isSavingStaff}
          style={{ opacity: signOff.canSubmit ? 1 : 0.5 }}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs py-3 px-4 rounded-xl flex items-center justify-center gap-2 mt-2 shadow-md shadow-blue-100 hover:shadow-lg transition-all tracking-wide"
        >
          <FiFileText className="text-base" />{" "}
          {/* 🛠️ FIXED: Changed undefined LuClipboardCheck to FiFileText */}
          <span className="text-xs font-bold text-white">
            {checklistTitle} Notes & Sign-off
          </span>
          <FiArrowRight className="text-sm mt-0.5" />
        </button>
      </div>

      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            {/* Backdrop */}
            <div
              className="absolute inset-0"
              onClick={() => setIsModalOpen(false)}
            />

            {/* Modal Container */}
            <div className="relative z-10 w-full max-w-4xl max-h-[95vh] bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
              <IncidentSelection onClose={() => setIsModalOpen(false)} />
            </div>
          </div>,
          document.body,
        )}

      {issuePhotoChoiceItem &&
        createPortal(
          <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            {/* Backdrop */}
            <div
              className="absolute inset-0"
              onClick={() => setIssuePhotoChoiceItem(null)}
            />

            {/* Modal */}
            <div className="relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl p-5">
              <div className="flex flex-col gap-1 mb-5">
                <h3 className="text-sm font-bold text-slate-800">
                  Add Issue Photo
                </h3>

                <p className="text-xs text-slate-400">
                  Choose how you want to add the photo.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Take Photo */}
                <button
                  type="button"
                  onClick={() => {
                    setIssuePhotoChoiceItem(issuePhotoChoiceItem);

                    setTimeout(() => {
                      issuePhotoCameraInputRef.current?.click();
                    }, 0);
                  }}
                  className="flex flex-col items-center justify-center gap-2 p-5 border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-blue-300 transition-colors"
                >
                  <div className="w-11 h-11 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center">
                    <FiCamera className="text-lg" />
                  </div>

                  <span className="text-xs font-semibold text-slate-700">
                    Take Photo
                  </span>

                  <span className="text-[10px] text-slate-400 text-center">
                    Use your camera
                  </span>
                </button>

                {/* Gallery */}
                <button
                  type="button"
                  onClick={() => {
                    setIssuePhotoChoiceItem(issuePhotoChoiceItem);

                    setTimeout(() => {
                      issuePhotoGalleryInputRef.current?.click();
                    }, 0);
                  }}
                  className="flex flex-col items-center justify-center gap-2 p-5 border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-blue-300 transition-colors"
                >
                  <div className="w-11 h-11 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center">
                    <FiFileText className="text-lg" />
                  </div>

                  <span className="text-xs font-semibold text-slate-700">
                    Select Photo
                  </span>

                  <span className="text-[10px] text-slate-400 text-center">
                    Choose from device
                  </span>
                </button>
              </div>

              <button
                type="button"
                onClick={() => setIssuePhotoChoiceItem(null)}
                className="w-full mt-4 py-2.5 text-xs font-semibold text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
            </div>
          </div>,
          document.body,
        )}
    </form>
  );
};

export default OpeningCheckForm;
